import {
    Directive,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ElementRef,
    Renderer2,
    inject,
} from '@angular/core';
import { Util } from 'app/models/util';
import { DmsThemeName } from 'app/models/dms-client-theme';

export type MatButtonVariant = 'flat' | 'stroked' | 'basic' | 'raised';

// HTML attribute added per variant (what Angular Material's selector targets)
const ATTR_MAP: Record<MatButtonVariant, string> = {
    flat:    'mat-flat-button',
    stroked: 'mat-stroked-button',
    basic:   'mat-button',
    raised:  'mat-raised-button',
};

// MDC CSS classes Angular Material 17 applies per variant
const MDC_CLASS_MAP: Record<MatButtonVariant, string[]> = {
    flat:    ['mat-mdc-unelevated-button'],
    stroked: ['mat-mdc-outlined-button', 'mdc-button--outlined'],
    basic:   ['mat-mdc-button'],
    raised:  ['mat-mdc-raised-button', 'mdc-button--raised'],
};

/**
 * Theme-wide default variant. Applied when no per-button entry resolves a variant.
 * Omit a theme to leave buttons at their HTML-defined variant.
 */
const THEME_DEFAULT_VARIANT_MAP: Partial<Record<DmsThemeName | 'default', MatButtonVariant>> = {
    panther_default: 'flat',
    panther_rose:    'flat',
};

/**
 * Per-button, per-theme variant config.
 * Add a new entry for each semantic button name your app needs.
 * Use 'default' as the fallback for themes not explicitly listed.
 */
const BUTTON_THEME_VARIANT_MAP: Record<string, Partial<Record<DmsThemeName | 'default', MatButtonVariant>>> = {
    delete: {
        panther_default: 'flat',
        default:         'flat',
    },
    save: {
        default: 'flat',
    },
    cancel: {
        default: 'stroked',
    },
    download: {
        default: 'flat',
    },
};

@Directive({
    standalone: true,
    selector: `
      button[mat-flat-button][matButtonVariant],    button[mat-flat-button][matButtonName],
      button[mat-stroked-button][matButtonVariant], button[mat-stroked-button][matButtonName],
      button[mat-button][matButtonVariant],         button[mat-button][matButtonName],
      button[mat-raised-button][matButtonVariant],  button[mat-raised-button][matButtonName]
    `
})
export class MatButtonVariantDirective implements OnInit, OnChanges {

    /** Explicit variant — takes priority over the theme map when set. */
    @Input() matButtonVariant?: MatButtonVariant;

    /** Semantic button name; resolves variant via BUTTON_THEME_VARIANT_MAP + active theme. */
    @Input() matButtonName?: string;

    private util = inject(Util);
    private el = inject<ElementRef<HTMLButtonElement>>(ElementRef);
    private renderer = inject(Renderer2);
    private WebInputParams = this.util.getWebComponentInputParams(); 

    ngOnInit(): void {
        this.resolveAndApply();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.matButtonVariant || changes.matButtonName) {
            this.resolveAndApply();
        }
    }

    private resolveAndApply(): void {
        const variant = this.resolveVariant();
        if (variant) {
            this.applyVariant(variant);
        }
    }

    private resolveVariant(): MatButtonVariant | null {
        // Explicit input wins
        if (this.matButtonVariant) {
            return this.matButtonVariant;
        }

        const themeName = this.WebInputParams.clientTheme.name ?? 'default';

        // Per-button name lookup wins over theme default
        if (this.matButtonName) {
            const themeMap = BUTTON_THEME_VARIANT_MAP[this.matButtonName];
            if (themeMap) {
                const result = themeMap[themeName] ?? themeMap['default'] ?? null;
                if (result) return result;
            }
        }

        // Theme-wide default fallback
        return THEME_DEFAULT_VARIANT_MAP[themeName] ?? null;
    }

    private applyVariant(variant: MatButtonVariant): void {
        const nativeEl = this.el.nativeElement;

        // Remove all variant attributes and MDC classes
        (Object.keys(ATTR_MAP) as MatButtonVariant[]).forEach(v => {
            this.renderer.removeAttribute(nativeEl, ATTR_MAP[v]);
            MDC_CLASS_MAP[v].forEach(cls => this.renderer.removeClass(nativeEl, cls));
        });

        // Set the new variant attribute and MDC classes
        this.renderer.setAttribute(nativeEl, ATTR_MAP[variant], '');
        MDC_CLASS_MAP[variant].forEach(cls => this.renderer.addClass(nativeEl, cls));
    }
}
